import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';

// Конфигурация тайтлов для всех страниц
const PAGE_TITLES: Record<string, string> = {
  // Client pages
  '/client/dashboard': 'Дашборд',
  '/client/accounts': 'Аккаунты OnlyFans',
  '/client/accounts/create': 'Добавить аккаунт',
  '/client/subscribers/proposals': 'Предложения',
  '/client/subscribers/orders': 'Заказы',
  '/client/offers': 'Офферы',
  '/client/offers/list': 'Список офферов',
  '/client/offers/campaigns': 'Кампании',
  '/client/offers/bills': 'Счета',
  '/client/statistics': 'Статистика',
  '/client/statistics/subscribers': 'Статистика подписчиков',
  '/client/statistics/transactions': 'Статистика транзакций',
  '/client/account': 'Аккаунт',
  '/client/account/wallet': 'Кошелек',
  '/client/account/settings': 'Настройки',

  // Client authentication
  '/client/sign-in': 'Вход',
  '/client/sign-up': 'Регистрация',
  '/client/forgot-password': 'Восстановление пароля',
  '/client/reset-password': 'Сброс пароля',

  // Partner pages
  '/partner': 'Партнерская панель',
  '/partner/sign-in': 'Вход для партнеров',
  '/partner/sign-up': 'Регистрация для партнеров',
  '/partner/forgot-password': 'Восстановление пароля',
  '/partner/reset-password': 'Сброс пароля',
};

// Функция для получения тайтла страницы
const getPageTitle = (pathname: string, params: any, accounts: any[], user: any): string => {
  // Обработка динамических страниц аккаунтов
  if (pathname.startsWith('/client/accounts/') && params.id) {
    const accountId = params.id;
    const account = accounts?.find(
      (acc) => acc.of_user_id?.toString() === accountId || acc.id === accountId
    );

    if (account && account.name) {
      // Если есть таб, добавляем его к тайтлу
      if (params.tab) {
        const tabTitles: Record<string, string> = {
          access: 'Доступ',
          public: 'Публичный контент',
          work: 'Рабочий контент',
          offer: 'Ваш оффер',
          campaigns: 'Кампании',
          bills: 'Счета',
          guarantee: 'Гарант',
        };
        const tabTitle = tabTitles[params.tab] || params.tab;
        return `${account.name} - ${tabTitle}`;
      }
      return account.name;
    }

    // Если аккаунт не найден в контексте, показываем общий тайтл
    if (params.tab) {
      const tabTitles: Record<string, string> = {
        access: 'Доступ',
        public: 'Публичный контент',
        work: 'Рабочий контент',
        offer: 'Ваш оффер',
        campaigns: 'Кампании',
        bills: 'Счета',
        guarantee: 'Гарант',
      };
      const tabTitle = tabTitles[params.tab] || params.tab;
      return `Аккаунт - ${tabTitle}`;
    }
    return 'Аккаунт';
  }

  // Обработка страницы создания заказа
  if (pathname.startsWith('/client/subscribers/proposals/create-order/')) {
    return 'Создание заказа';
  }

  // Поиск точного совпадения
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }

  // Поиск по префиксу для вложенных страниц
  for (const [route, title] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(route + '/')) {
      return title;
    }
  }

  // Дефолтный тайтл
  return 'OnlyU';
};

export const usePageTitleManager = () => {
  const location = useLocation();
  const params = useParams();
  const { accounts } = useAccount();
  const { user } = useAuth();

  useEffect(() => {
    // Не управляем тайтлами для страниц аккаунтов - они управляются специальным хуком
    if (location.pathname.startsWith('/client/accounts/')) {
      return;
    }

    const title = getPageTitle(location.pathname, params, accounts || [], user);
    const fullTitle = title === 'OnlyU' ? 'OnlyU' : `${title} | OnlyU`;

    document.title = fullTitle;
  }, [location.pathname, params, accounts, user]);
};

export default usePageTitleManager;
